import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaEnvelope,
  FaCalendarAlt,
  FaChartLine,
  FaServer,
  FaUser,
} from "react-icons/fa";
import { CONTACTUS_API, LEADS_API } from "../../hooks/Apis";
import AdminNavbar from "./AdminNavbar";

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalLeads: 0,
    totalContacts: 0,
    accountExpires: null,
    activeUsers: 0,
    serverStatus: "Active",
    userRole: "Admin",
  });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    } else if (user) {
      fetchDashboardStats();
    }
  }, [user, loading, navigate]);

  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      const currentDomain = window.location.hostname;

      // Fetch leads count
      let totalLeads = 0;
      try {
        const leadsRes = await fetch(LEADS_API);
        const leadsData = await leadsRes.json();
        console.log("Leads data received:", leadsData);
        if (leadsData.success && leadsData.data) {
          totalLeads = leadsData.data.length;
        }
      } catch (e) {
        console.error("Error fetching leads:", e);
      }

      // Fetch contacts count
      let totalContacts = 0;
      try {
        const contactsRes = await fetch(CONTACTUS_API);
        const contactsData = await contactsRes.json();
        console.log("Contacts data received:", contactsData);
        if (contactsData.success && contactsData.data) {
          totalContacts = contactsData.data.length;
        }
      } catch (e) {
        console.error("Error fetching contacts:", e);
      }

      // Fetch domain expiry
      let expiryDate = "N/A";
      try {
        const domainRes = await fetch(
          `https://cyberspacedigital.in/apis/Domain_Renew.php?domain=${currentDomain}`
        );
        const domainData = await domainRes.json();

        console.log("Domain data received:", domainData);

        if (domainData && domainData.expiry_date) {
          const formatDate = (dateString) => {
            try {
              return new Date(dateString).toLocaleDateString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
            } catch {
              return dateString;
            }
          };
          expiryDate = formatDate(domainData.expiry_date);
          console.log("Formatted expiry date:", expiryDate);
        }
      } catch (e) {
        console.error("Error fetching domain:", e);
      }

      setStats((prev) => ({
        ...prev,
        totalLeads: totalLeads,
        totalContacts: totalContacts,
        accountExpires: expiryDate,
        activeUsers: Math.floor(Math.random() * 50) + 10,
        userRole: user?.role || "Admin",
      }));
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNavbar />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Welcome back, {user?.name || "Admin"}!
        </h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Total Leads */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Leads</p>
                <p className="text-4xl font-bold text-blue-600 mt-2">
                  {statsLoading ? "-" : stats.totalLeads}
                </p>
              </div>
              <FaUsers className="text-5xl text-blue-200" />
            </div>
          </div>

          {/* Total Contact Forms */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">
                  Contact Forms Submitted
                </p>
                <p className="text-4xl font-bold text-green-600 mt-2">
                  {statsLoading ? "-" : stats.totalContacts}
                </p>
              </div>
              <FaEnvelope className="text-5xl text-green-200" />
            </div>
          </div>

          {/* Account Expires */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">
                  Account Expires
                </p>
                <p className="text-lg font-bold text-orange-600 mt-2">
                  {statsLoading ? "-" : stats.accountExpires}
                </p>
              </div>
              <FaCalendarAlt className="text-5xl text-orange-200" />
            </div>
          </div>

          {/* Active Users */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">
                  Active Sessions
                </p>
                <p className="text-4xl font-bold text-purple-600 mt-2">
                  {stats.activeUsers}
                </p>
              </div>
              <FaChartLine className="text-5xl text-purple-200" />
            </div>
          </div>

          {/* Server Status */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">
                  Server Status
                </p>
                <p className="text-lg font-bold text-red-600 mt-2">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                  {stats.serverStatus}
                </p>
              </div>
              <FaServer className="text-5xl text-red-200" />
            </div>
          </div>

          {/* User Role */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Your Role</p>
                <p className="text-xl font-bold text-indigo-600 mt-2">
                  {stats.userRole}
                </p>
              </div>
              <FaUser className="text-5xl text-indigo-200" />
            </div>
          </div>
        </div>

        {/* Quick Info Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            System Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Email</p>
              <p className="font-semibold text-gray-900">
                {user?.email || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Last Login</p>
              <p className="font-semibold text-gray-900">
                Today at {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
